/* global DataBasic:true, DataAdvanced:true */
import React from 'react';
import Utils from '../../js/utils.js';
import Graph from './graph.jsx';
import HeaderWrap from './header/head/headerWrap.jsx';
import InnerWrap from './header/inner/innerWrap.jsx';
import GraphFooter from './footer/graphFooter.jsx';
import TraceLegend from './footer/traceLegend.jsx';
import '../../js/dataBasic';
import '../../js/dataAdvanced';
import update from "immutability-helper";
import GraphCompare from './GraphCompare.jsx';
import {getAgrCount} from "./graphUtils.js";

class GraphContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            chart: null,
            graphData: null,
            yAxisLimit: null,
            stats: {},
            isUTC: false,
            zoomLevel: 1,
            customDebugAgrCount: 0,
            showCompare: false,
        };
    }

    setChartInstance = (key, data) => this.setState({ [key]: data });

    setUTC = isUTC => this.setState({ isUTC });

    switchYAxis = () => {
        const { chart } = this.state;
        const temp = chart.yAxis[0].max;
        const max = this.state.yAxisLimit;
        chart.yAxis[0].update({ max });
        this.setState({ yAxisLimit: temp });
    }

    loadGraph = ({ ip, ipDetails, callback }) => {
        const { epoch, probe, graphType, endPoint } = ipDetails;
        const end = endPoint === undefined ? Utils.getTimeNow() : endPoint;
        const chartWidth = Utils.getChartLineWidth(this.props.isMobile, this.props.isNarrow);
        const agrCount = getAgrCount({ipDetails, chartWidth });
        if(graphType === 'basic'){
            DataBasic.loadData(ip, end, epoch, agrCount, probe, callback)
        } else {
            DataAdvanced.loadData(ip, end, epoch, agrCount, probe, callback)
        }
    }

    setGraphData = (dataO, data, timestamp, stats) => {
        const componentProbe = Utils.getPropertyOfObject(['ipDetails', 'probe'], this.props);
        const componentIP = Utils.getPropertyOfObject(['selectedIP'], this.props);
        const dataProbe = Utils.getPropertyOfObject(['probe'], stats);
        const dataIP = Utils.getPropertyOfObject(['ip'], stats);
        // local probe or IP might have change before the callback returned
        if (componentProbe === dataProbe && componentIP === dataIP) {
            let graphDataNew = data;
            if (this.props.ipDetails.graphType === 'advanced') {
                graphDataNew = DataAdvanced.getDisplayData(data, this.props.theme.shadowReverse);
            }
            this.setState({
                originalData: dataO,
                graphData: graphDataNew,
                stats,
                showCompare: false,
            });
        }
    }

    getID = () => {
        const key = this.props.ipDetails.graphType === 'advanced'
            ?'advanced-graph-wrap'
            : 'basic-graph-wrap';

        return key+'-'+this.props.ipDetails.probe;
    }

    getTrueTimeFrame = () => {
        const data = this.state.graphData;
        const ipData = this.props.ipDetails;
        if (Utils.isSet(data) && Utils.isSet(data.epoch)) {
            return { to: ipData.endPoint, epoch: data.epoch };
        }
        return { to: ipData.endPoint, epoch: ipData.epoch };
    }

    setEpochFromZoom = (from, to, res = true) => {
        const epoch = to - from;
        this.props.setEpochFromZoom(epoch, to, res);
    }

    setShowCompare = (compare) =>{
        const newState = update(this.state, {
            showCompare: { $set: compare },
        });
        this.setState(newState);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.ipDetails != this.props.ipDetails ||
            prevProps.selectedIP != this.props.selectedIP ||
            prevProps.theme != this.props.theme ||
            prevProps.isNarrow != this.props.isNarrow ||
            prevState.customDebugAgrCount != this.state.customDebugAgrCount
        ) {
            this.loadGraph({ ip: this.props.selectedIP, ipDetails: this.props.ipDetails, callback: this.setGraphData });
        }
        if (Utils.isSet(this.props.generateURL)) {
            this.props.generateURL();
        }
        if(prevProps.ipDetails.probe !== this.props.ipDetails.probe || prevProps.selectedIP !== this.props.selectedIP) {
            this.setState({ showCompare: false });
        }
    }

    componentDidMount() {
        if (Utils.isSet(this.props.selectedIP)) {
            this.loadGraph({ ip: this.props.selectedIP, ipDetails: this.props.ipDetails, callback: this.setGraphData });
        }
    }

    render() {
        const hide = Utils.getPropertyOfObject(['hideGraphInfo'], this.props, false);
        const sharedGraphProps = {
            chartWidth: Utils.getChartLineWidth(this.props.isMobile, this.props.isNarrow),
            isUTC: this.state.isUTC,
            noZoom: Utils.getPropertyOfObject(['noZoom'], this.props, false),
            zoomLevel: this.state.zoomLevel,
            graphType: this.props.ipDetails.graphType,
            ipDetails: this.props.ipDetails,
            selectedIP: this.props.selectedIP,
            isMobile: this.props.isMobile,
            theme: this.props.theme,
        }
        return (
            <div className='graph-wrap' id={this.getID()}>
                {!hide && <HeaderWrap {...this.props} switchYAxis={this.switchYAxis} />}
                <div className='graph-inner-wrap'>
                    {!hide &&
                        <InnerWrap
                            {...this.props}
                            setUTC={this.setUTC}
                            isUTC={this.state.isUTC}
                            setZoom={val => this.setChartInstance('zoomLevel', val)}
                            zoomLevel={this.state.zoomLevel}
                        />
                    }
                    <div className='graph-inner__custom-count'>
                        <input
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    this.setState({ customDebugAgrCount: e.target.value });
                                }
                            }}
                        />
                    </div>
                    <Graph
                        graphData={ this.state.graphData}
                        originalData={ this.state.originalData}
                        setChartInstance={ this.setChartInstance}
                        setEpochFromZoom={ this.setEpochFromZoom}
                        stats={ this.state.stats}
                        {...sharedGraphProps } />
                    {!hide &&
                        <GraphFooter
                            {...this.props}
                            chart={this.state.chart}
                            graphData={this.state.graphData}
                            stats={this.state.stats}
                            pxWidth={Utils.getChartLineWidth(this.props.isMobile, this.props.isNarrow)}
                        />
                    }
                </div>
                {!hide && <GraphCompare
                    key={`${this.props.selectedIP}-${this.props.ipDetails.probe}`}
                    {...sharedGraphProps}
                    setEpochFromZoom={this.setEpochFromZoom}
                    setShowCompare={this.setShowCompare}
                    probesList={this.props.probesList}
                />}
                {!hide && !this.state.showCompare &&
                    <TraceLegend
                        {...this.props}
                        chart={this.state.chart}
                        trueTime={this.getTrueTimeFrame()}
                    />
                }
            </div>
        );
    }
}

export default GraphContainer;
